import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "../api/client";
import { Card, Badge, EmptyState } from "../components/shared";
import { useAuth } from "../contexts/AuthContext";
import {
  BookOpen, FileText, FileSpreadsheet, Download, Search, Filter, Calendar,
} from "lucide-react";
import { toast } from "sonner";

const INPUT = "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-11 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";

const CATEGORY_ORDER = ["guides", "procedures", "templates", "reports", "platform"];
const CATEGORY_COLORS = {
  guides: "bg-brand/10 text-brand",
  procedures: "bg-[#2d8a56]/10 text-[#2d8a56]",
  reports: "bg-[#e3a03b]/15 text-[#8a5a10]",
  templates: "bg-[#0b1721]/5 text-[#0b1721]",
  platform: "bg-[#6d28d9]/10 text-[#6d28d9]",
};

export default function Documentation() {
  const { user } = useAuth();
  const isPlatform = user?.role === "super_admin";
  const basePath = isPlatform ? "/platform/documentation" : "/documentation";

  const [data, setData] = useState({ categories: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [formatFilter, setFormatFilter] = useState("all");
  const [period, setPeriod] = useState("30d");
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(basePath);
        setData(d);
      } catch (err) {
        toast.error(apiError(err, "Erreur"));
      } finally {
        setLoading(false);
      }
    })();
  }, [basePath]);

  const filtered = useMemo(() => {
    let list = data.items || [];
    if (categoryFilter !== "all") list = list.filter((d) => d.category === categoryFilter);
    if (formatFilter !== "all") list = list.filter((d) => d.format === formatFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((d) =>
        (d.title || "").toLowerCase().includes(s) ||
        (d.description || "").toLowerCase().includes(s)
      );
    }
    return list;
  }, [data.items, categoryFilter, formatFilter, search]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((d) => { (g[d.category] ||= []).push(d); });
    return g;
  }, [filtered]);

  const download = async (doc) => {
    setDownloading(doc.id);
    try {
      const params = doc.dynamic ? { period } : {};
      const res = await api.get(`${basePath}/${doc.id}`, { params, responseType: "blob" });
      const blob = new Blob([res.data], {
        type: doc.format === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mediflow_${doc.id}_${new Date().toISOString().slice(0, 10)}.${doc.format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Téléchargement lancé");
    } catch (err) {
      toast.error(apiError(err, "Erreur de téléchargement"));
    } finally {
      setDownloading(null);
    }
  };

  const categoryCounts = (data.items || []).reduce((acc, d) => {
    acc[d.category] = (acc[d.category] || 0) + 1;
    return acc;
  }, {});

  const hasDynamicDocs = filtered.some((d) => d.dynamic);

  return (
    <div data-testid="documentation-page">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl text-ink flex items-center gap-2">
          <BookOpen className="w-7 h-7 text-brand" /> Documentation
        </h1>
        <p className="text-ink-muted text-sm mt-1">
          {isPlatform
            ? "Ressources plateforme Super Admin — guides, annuaires, rapports agrégés."
            : "Guides, procédures, modèles et rapports prêts à télécharger en Word ou Excel."}
        </p>
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4 flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input data-testid="doc-search" className={`${INPUT} pl-10`} placeholder="Rechercher un document…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1 p-1 rounded-xl bg-subtle flex-wrap">
          <CategoryChip k="all" label={`Toutes (${data.items?.length || 0})`} active={categoryFilter === "all"} onClick={() => setCategoryFilter("all")} />
          {CATEGORY_ORDER.filter((c) => categoryCounts[c]).map((c) => (
            <CategoryChip key={c} k={c} label={`${data.categories[c] || c} (${categoryCounts[c]})`}
              active={categoryFilter === c} onClick={() => setCategoryFilter(c)} />
          ))}
        </div>
        <div className="flex gap-1 p-1 rounded-xl bg-subtle">
          {[
            { k: "all", label: "Tous formats" },
            { k: "docx", label: "Word" },
            { k: "xlsx", label: "Excel" },
          ].map((f) => (
            <CategoryChip key={f.k} k={f.k} label={f.label} active={formatFilter === f.k} onClick={() => setFormatFilter(f.k)} />
          ))}
        </div>
        {hasDynamicDocs && (
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-ink-muted" />
            <select data-testid="doc-period" value={period} onChange={(e) => setPeriod(e.target.value)}
              className="h-11 rounded-xl border border-[var(--border-default)] bg-white px-3 text-sm focus:border-brand outline-none">
              <option value="7d">7 derniers jours</option>
              <option value="30d">30 derniers jours</option>
              <option value="90d">90 derniers jours</option>
              <option value="1y">12 derniers mois</option>
            </select>
          </div>
        )}
      </Card>

      {loading ? (
        <div className="py-16 text-center text-ink-muted">Chargement…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-4"><EmptyState icon={BookOpen} title="Aucun document" subtitle="Modifiez les filtres ou la recherche." /></Card>
      ) : (
        <div className="space-y-6">
          {CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((c) => (
            <div key={c}>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="font-heading font-bold text-xl text-ink">{data.categories[c] || c}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${CATEGORY_COLORS[c] || "bg-subtle text-ink-muted"}`}>
                  {grouped[c].length}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`doc-cat-${c}`}>
                {grouped[c].map((d) => (
                  <DocCard key={d.id} doc={d} onDownload={() => download(d)} loading={downloading === d.id} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryChip({ k, label, active, onClick }) {
  return (
    <button data-testid={`doc-filter-${k}`} onClick={onClick}
      className={`h-9 px-3 rounded-lg text-sm font-semibold transition-colors ${active ? "bg-white shadow-sm text-brand" : "text-ink-muted"}`}>
      {label}
    </button>
  );
}

function DocCard({ doc, onDownload, loading }) {
  const isExcel = doc.format === "xlsx";
  const Icon = isExcel ? FileSpreadsheet : FileText;
  const accent = isExcel ? "text-[#107C41]" : "text-[#2b579a]";
  return (
    <Card className="p-4 flex flex-col gap-3 hover:shadow-md transition-shadow" data-testid={`doc-${doc.id}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-xl bg-subtle ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-ink leading-tight">{doc.title}</div>
          <div className="text-xs text-ink-muted mt-1 line-clamp-2">{doc.description}</div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 mt-auto">
        <div className="flex flex-wrap gap-1">
          <Badge tone={isExcel ? "warning" : "brand"}>{isExcel ? "Excel" : "Word"}</Badge>
          {doc.dynamic && <Badge tone="info">Dynamique</Badge>}
        </div>
        <button onClick={onDownload} disabled={loading} data-testid={`doc-dl-${doc.id}`}
          className="h-10 px-3 rounded-xl bg-brand hover:bg-brand/90 text-white font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50">
          <Download className="w-4 h-4" /> {loading ? "…" : "Télécharger"}
        </button>
      </div>
    </Card>
  );
}
